<template>
  <div>
    <!-- Controls list -->
    <div v-if="!isControlFormActive">
      <b-row>
        <b-col
          class="mb-2"
          cols="12"
        >
          <h5 class="mb-0">
            {{ $t('message.activity_title') }}
            <br>
            <small
              class="text-muted"
            >{{ $t('message.activity_desc') }}
            </small>
          </h5>
        </b-col>
      </b-row>
      <b-row
        v-for="(row, index) in descriptionInput"
        :key="index"
      >
        <b-col
          v-for="(singleInput, loopIndex) in row.singleRow.rowInputs"
          :key="loopIndex"
          :md="singleInput.md"
        >
          <GlobalSingleInput :input="singleInput" :readOnly="readOnly" />
        </b-col>
      </b-row>
  
      <div>
        <h5>{{ $t('label.activities') }}</h5>
        <draggable v-model="controls" tag="ul" class="draggable-list" :disabled="readOnly">
          <li v-for="(control, idx) in controls" :key="idx" class="draggable-item">
            <feather-icon icon="MenuIcon" class="menu-icon" />
            <span class="mr-1">{{ `${idx + 1}.` }}</span>
            <v-select
              v-model="controls[idx]"
              class="control-select"
              label="title"
              :options="controlOptions"
              :reduce="(option) => option.value"
              :placeholder="$t('placeholder.controlSelect')"
              :append-to-body="true"
              :selectable="(option) => !controls.includes(option.value)"
              :disabled="readOnly"
            />
            <small v-show="!control" class="text-danger ml-1">{{ validationMessage }}</small>
            <b-button v-show="!!control" variant="outline-secondary" class="ml-2" :disabled="readOnly" @click="activateControlForm(idx, controlOptionsComplete.find(e => e._id === control))">
              <feather-icon icon="EditIcon" class="mr-25" />
              <span>{{ $t('Edit') }}</span>
            </b-button>
            <b-button variant="outline-success" class="ml-2" :disabled="readOnly" @click="activateControlForm(idx)">
              <feather-icon icon="PlusIcon" class="mr-25" />
              <span>{{ $t('message.create') }}</span>
            </b-button>
            <b-button variant="outline-danger" class="ml-2" :disabled="readOnly" @click="removeControl(idx)">
              <feather-icon icon="XIcon" class="mr-25" />
              <span>{{ $t('message.delete') }}</span>
            </b-button>
          </li>
        </draggable>
      </div>
  
      <b-button class="mt-1" variant="primary" :disabled="readOnly" @click="addControl">
        <feather-icon icon="PlusIcon" class="mr-25" />
        <span>{{ $t('message.Add_New_Activity') }}</span>
      </b-button>
    </div>

    <!-- New control form -->
    <div v-else>
      <ControlForm :data="newControl" :isSubmitting="isCreatingControl || isUpdatingControl" :isFromRisk="true" :isFromRiskEdit="isEditMode" @cancel="resetNewControlData" @submit="handleControl" />
    </div>
  </div>
</template>

<script>
import { heightTransition } from '@core/mixins/ui/transition'
import Ripple from 'vue-ripple-directive'
import GlobalSingleInput from '@/views/components/form/GlobalSingleInput.vue'
import { convertInputsToKeyValuePair } from '@/helpers/objectHelper'
import i18n from '@/libs/i18n'
import { set, ref, onMounted, computed } from '@vue/composition-api'
import draggable from 'vuedraggable'
import vSelect from 'vue-select'
import { useLambda } from '@/views/habit/awsLambda'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import ControlForm from '@/views/organization/control/shared/Form.vue'
import useControlAdd from '@/views/organization/control/add/useControlAdd'
import useControlEdit from '@/views/organization/control/edit/useControlEdit'
import { oid } from '@/helpers/mongoHelpers'

export default {
  directives: {
    Ripple,
  },
  components: {
    GlobalSingleInput,
    draggable,
    vSelect,
    ControlForm,
  },
  mixins: [heightTransition],
  props: {
    data: { type: Object, default: {} },
    readOnly: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const { showErrorMessage } = useNotifications()
    const { getItems } = useLambda()
    const { create: createNewControl, control: newControl, isSubmitting: isCreatingControl } = useControlAdd()
    const { update: updateNewControl, isSubmitting: isUpdatingControl } = useControlEdit()
    const userData = store.state?.userStore?.userData
    const controls = ref([])
    const controlOptions = ref([])
    const controlOptionsComplete = ref([])
    const validationMessage = computed(() => i18n.t('label.required'))    
    const isControlFormActive = ref(false)
    const controlActiveIndex = ref(null)
    const isEditMode = ref(false)

    const addControl = () => {
      controls.value.push('')
    }

    const removeControl = (idx) => {
      controls.value.splice(idx, 1)
    }

    const activateControlForm = (index, controlData) => {
      if (controlData) {
        newControl.value = {
          _id: controlData._id,
          name: controlData.name,
          responsible: controlData.responsible || '',
          responsible_id: controlData.responsible_id || null,
          behaviours: controlData.behaviours?.map(e => String(e)) || []
        }
        isEditMode.value = true
      } else {
        isEditMode.value = false
      }
      controlActiveIndex.value = index
      isControlFormActive.value = true
    }

    const resetNewControlData = () => {
      isControlFormActive.value = false
      newControl.value = { responsible_id: null }
    }

    const handleControl = async (payload) => {
      try {
        if (isEditMode.value) {
          await updateNewControl(payload, false)
          await fetchControls()
        } else {
          const newControlId = await createNewControl(payload, false)
          if (newControlId) {
            const newOption = {
              title: payload.name,
              value: newControlId.toString(),
            }
  
            controlOptions.value.push(newOption);
            controls.value[controlActiveIndex.value] = newOption.value;
          }
        }
        resetNewControlData()
      } catch (error) {
        console.error(error)
        showErrorMessage(isEditMode.value ? i18n.t('message.control_edit_error') : i18n.t('message.control_add_error'))
      }
    }

    const fetchControls = async () => {
      try {
        const query = {
        client_id: oid(userData?.client?.$oid),
        deleted: { $ne: true }
      }

        const items = await getItems({ collection: 'control', query, options: { sort: { name: 1 } } })
        
        items?.forEach(e => {
          e._id = e._id.toString()
          e.responsible_id = e.responsible_id ? String(e.responsible_id) : null
        })
        
        controlOptionsComplete.value = items || []
        controlOptions.value = items?.map(e => ({ title: e.name, value: e._id })) || []
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.data_fetch_error'))
      }
    }

    onMounted(async () => {
      fetchControls()
    })

    return {
      controls,
      controlOptions,
      controlOptionsComplete,
      addControl,
      removeControl,
      validationMessage,
      newControl,
      createNewControl,
      isCreatingControl,
      isUpdatingControl,
      resetNewControlData,
      isControlFormActive,
      activateControlForm,
      handleControl,
      isEditMode,
    }
  },
  data() {
    return {
      descriptionInput: [
        {
          singleRow: {
            rowAttributes: {},
            rowInputs: [
              {
                label: i18n.t('label.description'),
                type: 'textarea',
                key: 'description',
                rules: '',
                placeholder: i18n.t('placeholder.activity_desc'),
                value: '',
                description: i18n.t('message.process_desc'),
              },
            ],
          },
        },
      ],
      items: [
        {
          singleRow: {
            rowAttributes: {},
            rowInputs: [
              {
                md: 8,
                label: i18n.t('message.activity_no_1'),
                notDeletedIndex: 0,
                type: 'text',
                key: 'activity_1',
                rules: 'required',
                placeholder: i18n.t('placeholder.activity'),
                value: '',
              },
            ],
          },
        },
      ],
    }
  },
  watch: {
    data: {
      handler(value) {
        this.descriptionInput[0].singleRow.rowInputs[0].value = value.description
        if (value.list) {
          this.controls = value.list
          // this.items = []
          // value.list.forEach((activity, index) => {
          //   this.repeatAgain()
          //   this.items[index].singleRow.rowInputs[0].value = activity
          // })
          // this.trAddHeight(value.list.length * 40)
        }
      },
      deep: true,
      immediate: true,
    },
    items: {
      handler(value) {
        this.$emit('updActivities', value)
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.initTrHeight()
  },
  created() {
    window.addEventListener('resize', this.initTrHeight)
  },
  destroyed() {
    window.removeEventListener('resize', this.initTrHeight)
  },
  methods: {
    repeatAgain() {
      const notDeletedIndex = this.items.filter(e => !e.singleRow.rowInputs[0].deleted).length
      this.items.push({
        singleRow: {
          rowAttributes: {},
          rowInputs: [
            {
              md: 8,
              label: i18n.tc('message.activity_no', 0, { count: `${notDeletedIndex + 1}` }),
              notDeletedIndex,
              type: 'text',
              key: `activity_${this.items.length + 1}`,
              rules: 'required',
              placeholder: i18n.t('placeholder.activity'),
              value: '',
            },
          ],
        },
      })

      // this.$nextTick(() => {
      //   if (this.items.length === 1) { this.trAddHeight(this.$refs.row[0].offsetHeight + 10) } else this.trAddHeight(this.$refs.row[0].offsetHeight)
      // })
    },
    removeItem(index) {
      set(this.items[index].singleRow.rowInputs[0], 'deleted', true)
      
      if (this.items.length) this.trTrimHeight(this.$refs.row[0].offsetHeight)
      else this.initTrHeight()

      // Update label and notDeletedIndex of activities
      // If there are no more activities, add a new blank activity
      const activitiesNotDeleted = this.items.filter(e => !e.singleRow.rowInputs[0].deleted).length

      if (!activitiesNotDeleted) this.repeatAgain()
      else {
        let notDeletedIndex = 0
        this.items.forEach(e => {
          if (!e.singleRow.rowInputs[0].deleted) {
            e.singleRow.rowInputs[0].label = i18n.tc('message.activity_no', 0, { count: `${notDeletedIndex + 1}` })
            e.singleRow.rowInputs[0].notDeletedIndex = notDeletedIndex
            notDeletedIndex++
          }
        })
      }
    },
    initTrHeight() {
      this.trSetHeight(null)
      // this.$nextTick(() => {
      //   this.trSetHeight(
      //     this.$refs.form.scrollHeight + this.items.length === 0 ? 0 : 90,
      //   )
      // })
    },
    getData() {
      return {
        activities: {
          ...convertInputsToKeyValuePair(this.descriptionInput),
          list: this.controls,
        },
        isControlFormActive: this.isControlFormActive
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.repeater-form {
  overflow: hidden;
  transition: 0.35s height;
}

.draggable-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.draggable-item {
  display: flex;
  align-items: center;
  padding: 10px;
  margin: 5px 0;
  border: 1px solid #ddd;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.draggable-item:hover {
  background-color: #f9f9f9;
}

.menu-icon {
  margin-right: 14px;
  cursor: move;
}

.control-select {
  flex-grow: 1;
}
</style>
